// Utilities to normalize potentially insecure image URLs and sanitize HTML snippets
// Strategy:
// - If an image URL starts with http:// => wrap via backend proxy: /api/proxy/image?url=ENCODED
// - If it starts with https:// => leave as-is
// - If it starts with // (protocol-relative) => assume https
// - data: URLs and relative paths are preserved
// - For srcset, rewrite each candidate URL using same logic
// - For HTML snippets, parse in a detached DOM, rewrite <img src> and srcset

export function normalizeImageUrl(url) {
  if (!url || typeof url !== 'string') return url
  const trimmed = url.trim()
  if (trimmed.startsWith('data:')) return trimmed
  if (trimmed.startsWith('//')) return 'https:' + trimmed
  if (trimmed.startsWith('http://')) {
    const proxied = '/api/proxy/image?url=' + encodeURIComponent(trimmed)
    return proxied
  }
  // https:// or relative
  return trimmed
}

export function normalizeSrcSet(srcset) {
  if (!srcset || typeof srcset !== 'string') return srcset
  try {
    const parts = srcset
      .split(',')
      .map(p => p.trim())
      .filter(Boolean)
      .map(entry => {
        const [url, descriptor] = entry.split(/\s+/, 2)
        const safeUrl = normalizeImageUrl(url)
        return descriptor ? `${safeUrl} ${descriptor}` : safeUrl
      })
    return parts.join(', ')
  } catch {
    return srcset
  }
}

export function normalizeHtmlImages(html) {
  if (!html || typeof html !== 'string') return html
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const imgs = doc.querySelectorAll('img')
    imgs.forEach(img => {
      const src = img.getAttribute('src')
      if (src) img.setAttribute('src', normalizeImageUrl(src))
      const srcset = img.getAttribute('srcset')
      if (srcset) img.setAttribute('srcset', normalizeSrcSet(srcset))
      // Optional: referrerpolicy to reduce leakage
      if (!img.getAttribute('referrerpolicy')) img.setAttribute('referrerpolicy', 'no-referrer')
      // Ensure loading behavior
      if (!img.getAttribute('loading')) img.setAttribute('loading', 'lazy')
      if (!img.getAttribute('decoding')) img.setAttribute('decoding', 'async')
    })
    // Serialize back
    return doc.body.innerHTML
  } catch (e) {
    return html
  }
}

// Helper to map an array of media objects { src } safely
export function normalizeMediaArray(mediaArray) {
  if (!Array.isArray(mediaArray)) return mediaArray
  return mediaArray.map(m => ({ ...m, src: normalizeImageUrl(m?.src || '') }))
}
